import {
  Box,
  Typography,
  useTheme,
  Button,
  CircularProgress,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import React, { useState, useContext } from "react";
import { tokens } from "../../theme";
import { UserIDContext, UserEmailContext } from "../../App";
import { usePesapal } from "./usePesapal";

function PesapalPackageCard({
  cardTitle,
  price,
  perMonth,
  intro,
  bullets,
  currency,
  paymentAmount,
  backgroundColor,
  buttonText,
  buttonColor,
  disabled,
  billingCycle,
}) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const userID = useContext(UserIDContext);
  const userEmail = useContext(UserEmailContext);
  const { mintPriceToken, submitOrder, loading, error } = usePesapal();
  const [processingPayment, setProcessingPayment] = useState(false);

  const CheckedPoint = ({ bullet }) => {
    return (
      <Box sx={{ display: "flex", flexDirection: "row", gap: "10px" }}>
        <CheckCircleIcon fontSize="small" />
        <Typography>{bullet}</Typography>
      </Box>
    );
  };

  const handlePesapalPayment = async () => {
    if (!paymentAmount || !currency || !userID) {
      console.error("Missing required payment data");
      return;
    }

    setProcessingPayment(true);

    try {
      // Generate unique order ID
      const orderId = `LT-${userID.substring(0, 8)}-${Date.now()}`;

      // Determine subscription duration based on billing cycle
      const duration = billingCycle === "yearly" ? 365 : 31;

      // Get the callback URL (current origin + callback path)
      const callbackUrl = `${window.location.origin}/pesapal/callback`;

      const paymentData = {
        id: orderId,
        amount: paymentAmount,
        currency: currency,
        description: `LoanTabs ${cardTitle} Subscription - ${billingCycle}`,
        callback_url: callbackUrl,
        billing_address: {
          email_address: userEmail,
          phone_number: "",
          first_name: userEmail?.split("@")[0] || "Customer",
          last_name: "",
        },
        // Custom metadata for subscription processing
        metadata: {
          userId: userID,
          packageType: cardTitle,
          billingCycle: billingCycle,
          duration: duration,
          currency: currency,
          amount: paymentAmount,
        },
      };

      // Optional hardening: mint a short-lived signed price token.
      // If minting fails (misconfig/network), we proceed without the token
      // so the payment flow does not break.
      try {
        const tokenResult = await mintPriceToken({
          orderId,
          userId: userID,
          packageType: cardTitle,
          billingCycle,
          amount: paymentAmount,
          currency,
        });
        if (tokenResult?.token) {
          paymentData.price_token = tokenResult.token;
          paymentData.metadata.priceToken = tokenResult.token;
        }
      } catch (tokenErr) {
        // Intentionally non-fatal
        console.warn("Proceeding without price token");
      }

      const response = await submitOrder(paymentData);

      if (response && response.redirect_url) {
        // Store payment info in localStorage for callback processing
        localStorage.setItem(
          "pesapal_payment",
          JSON.stringify({
            orderId: orderId,
            orderTrackingId: response.order_tracking_id,
            merchantReference: response.merchant_reference,
            userId: userID,
            packageType: cardTitle,
            billingCycle: billingCycle,
            duration: duration,
            amount: paymentAmount,
            currency: currency,
          })
        );

        // Redirect to Pesapal payment page
        window.location.href = response.redirect_url;
      } else {
        console.error("No redirect URL received from Pesapal");
        setProcessingPayment(false);
      }
    } catch (err) {
      console.error("Payment initiation failed:", err);
      setProcessingPayment(false);
    }
  };

  return (
    <Box
      minWidth={"260px"}
      sx={{
        p: "30px",
        backgroundColor,
        color: colors.blueAccent[900],
        borderRadius: "20px",
        justifyContent: "space-between",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box>
        <Typography mb="15px" sx={{ fontSize: "20px", fontWeight: 700 }}>
          {cardTitle}
        </Typography>
        <Box
          sx={{ display: "flex", flexDirection: "row", alignItems: "center" }}
        >
          {currency && (
            <Typography mt="15px" mr="8px" sx={{ fontWeight: 700 }}>
              {currency}
            </Typography>
          )}
          <Typography sx={{ fontSize: "40px", fontWeight: 700 }}>
            {typeof price === "number" ? `$${price}` : price}
          </Typography>
          <Typography mt="15px" sx={{ fontWeight: 700 }}>
            {perMonth}
          </Typography>
        </Box>
        <Typography mt="10px" mb="20px" sx={{ fontWeight: 600 }}>
          {intro}
        </Typography>
        {bullets &&
          bullets.map((item, index) => (
            <Box mt="10px" key={index}>
              <CheckedPoint bullet={item} />
            </Box>
          ))}
      </Box>
      <Box mt="20px">
        {paymentAmount && (
          <Button
            sx={{ backgroundColor: buttonColor, width: "100%" }}
            disabled={disabled || loading || processingPayment}
            variant="contained"
            onClick={handlePesapalPayment}
          >
            {loading || processingPayment ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={20} color="inherit" />
                <span>Processing...</span>
              </Box>
            ) : (
              buttonText
            )}
          </Button>
        )}
        {error && (
          <Typography
            color="error"
            variant="caption"
            sx={{ mt: 1, display: "block" }}
          >
            Payment failed. Please try again.
          </Typography>
        )}
      </Box>
    </Box>
  );
}

export default PesapalPackageCard;
